
import {defineComponent} from "vue";
import {
  CodeDescriptionRestDto,
  MultilingualFieldRestDto,
  ShoppingCartDiscountSchemeRestDto,
} from "@/api/pharma-cpc-mgmt/models";
import {defineRule} from "vee-validate";
import CatalogBuildStatus from "@/components/layouts/catalog/CatalogBuildStatus.vue";
import DiscountPricelistSection from "@/components/layouts/catalog/discounts-pricelists/DiscountPriceListSection.vue";
import ProductFilter from "@/components/layouts/catalog/product-filter/ProductFilter.vue";
import MultilingualSwitcher from "@/components/UI/MultilingualSwitcher.vue";
import {I18nContext} from "@/context/I18nContext";
import {useToast} from "vue-toastification";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {isEmptyString} from "@/helpers/functions/string";
import ButtonProductFilterPreview from "@/components/layouts/catalog/ButtonProductFilterPreview.vue";
import {StatusEnum} from "@/api/enums/status-enum";
import {EnumClassNameCpcEnum} from "@/models/enum/EnumClassNameCpcEnum";
import {UIStateDto} from "@/dtos/UIStateDto";
import I18nInputText from "@/components/UI/I18n/I18nInputText.vue";
import {ShoppingCartDiscountTypeEnum} from "@/api/enums/shopping-cart-discount-type-enum";
import {ShoppingCartDiscountSchemeRestService} from "@/services/rest/cpc-mgmt/ShoppingCartDiscountSchemeRestService";
import {UtilityRestService} from "@/services/rest/cms-utility/UtilityRestService";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {FormMixin} from "@/components/base/FormMixin";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {
  ShoppingCartDiscountSchemeRestDtoModel
} from "@/models/api/pharma-cpc-mgmt/ShoppingCartDiscountSchemeRestDtoModel";
import {ClientGroupManagementService} from "@/services/rest/idm-user/ClientGroupManagementService";
import {formatDateObjectToDateString} from "@/helpers/functions/date-time";

defineRule("check_date", (value: any, [begin, end]: any) => {
  if ((begin && end) && begin > end) {
    return 'De einddatum moet gelijk aan of later zijn';
  }
  return true;
});

export default defineComponent({
  name: "ShoppingCartDiscountsDetail",
  mixins: [FormMixin],
  components: {
    GenericFormWrapper,
    I18nInputText,
    ButtonProductFilterPreview,
    BaseTitle,
    MultilingualSwitcher,
    ProductFilter,
    DiscountPricelistSection,
    CatalogBuildStatus
  },
  props: ['mode'],
  data() {
    return {
      id: this.$route.params.id,
      returnRoute: 'discounts-price-lists' as string,

      discountScheme: ShoppingCartDiscountSchemeRestDtoModel.createWithDefaults() as ShoppingCartDiscountSchemeRestDto,
      promoMessageTimeoutId: null as number | null,
      promoMessage: MultilingualRestDtoModel.createWithDefaults(),

      activeLanguage: I18nContext.getDefaultApiLanguageKey(),

      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
      discountTypeOptions: [] as CodeDescriptionRestDto[],
      clientGroups: [] as CodeDescriptionRestDto[],
    }
  },
  mounted() {
    this.initialize();
  },
  computed: {
    discountLabel(): string {
      if (this.discountScheme.discount_value && this.discountScheme.discount_value > 0) {
        if (this.discountScheme.type_code === ShoppingCartDiscountTypeEnum.PERCENTAGE_FOR_SHOPPING_CART_PART) {
          return `- ${this.discountScheme.discount_value}%`
        }
        return `€ -${this.discountScheme.discount_value}`
      }
      return '';
    },
    statusOptions(): CodeDescriptionRestDto[] {
      return [
        {
          code: StatusEnum.PUBLISHED,
          description: this.$t('published'),
        },
        {
          code: StatusEnum.NOT_PUBLISHED,
          description: this.$t('notPublished'),
        },
      ];
    },
  },
  methods: {
    isEmptyString,
    async doInitialize(): Promise<void> {
      this.discountTypeOptions = await EnumRestService.getInstance().getCpcEnumValuesByClassname(EnumClassNameCpcEnum.SHOPPING_CART_DISCOUNT_TYPE);
      const groups = await ClientGroupManagementService.getInstance().findClientGroups();
      this.clientGroups = [{code: '', description: this.$t('none')}, ...groups];
    },
    async doSubmit(): Promise<void> {
      if (this.mode === PageDetailModeEnum.NEW) {
        await ShoppingCartDiscountSchemeRestService.getInstance().createShoppingCartDiscountScheme(this.discountScheme);
      } else {
        await ShoppingCartDiscountSchemeRestService.getInstance().updateShoppingCartDiscountScheme(this.id, this.discountScheme);
      }
    },
    async doReload(): Promise<void> {
      if (this.mode === PageDetailModeEnum.NEW) {
        this.discountScheme = ShoppingCartDiscountSchemeRestDtoModel.createWithDefaults();
      } else {
        this.discountScheme = await ShoppingCartDiscountSchemeRestService.getInstance().findShoppingCartDiscountScheme(this.id);
      }
    },
    async afterSave() {
      if (this.mode === PageDetailModeEnum.NEW) {
        await this.$router.push({name: this.returnRoute});
      } else {
        await handleSavedSuccessfully();
        await this.reloadContent();
      }
    },
    removeDiscountSchemePeriod(): void {
      this.discountScheme.begin_date = formatDateObjectToDateString(new Date());
      this.discountScheme.end_date = undefined;
      this.onFormDataChanged();
    },
    promoMessageUpdated(value: any): void {
      this.discountScheme.promo_message = value;
      this.truncatePromoMessage();
      this.onFormDataChanged();
    },
    truncatePromoMessage(): void {
      if (this.promoMessageTimeoutId !== null) {
        clearTimeout(this.promoMessageTimeoutId);
      }

      this.promoMessageTimeoutId = setTimeout(() => {
        Object.keys(this.promoMessage).forEach(async (key: string) => {
          if (this.discountScheme.promo_message) {
            let value = this.discountScheme.promo_message[key as keyof MultilingualFieldRestDto];
            if (!isEmptyString(value)) {
              value = await UtilityRestService.getInstance()
                .truncateHtml(value, 100);
            }

            this.discountScheme.promo_message[key as keyof MultilingualFieldRestDto] = value;
          }
        });
      }, 1000);
    },
  },
});
